/**
 * User-presentable explanations for files the browser cannot use (pure).
 */
const CODEC_NAMES: Record<string, string> = {
  prores: 'ProRes',
  hevc: 'HEVC (H.265)',
  av1: 'AV1',
  vp8: 'VP8',
  vp9: 'VP9',
  avc: 'H.264',
  ac3: 'Dolby Digital (AC-3)',
  eac3: 'Dolby Digital Plus (E-AC-3)',
  dts: 'DTS',
};

export function codecName(codec: string | null | undefined): string {
  if (!codec) return 'an unknown codec';
  return CODEC_NAMES[codec] ?? codec.toUpperCase();
}

export function undecodableVideoMessage(codec: string | null | undefined): string {
  return `This video uses ${codecName(codec)}, which this browser can't decode. Convert it to H.264 or HEVC (e.g. with HandBrake) and import it again.`;
}

export function undecodableAudioMessage(codec: string | null | undefined): string {
  return `The audio uses ${codecName(codec)}, which this browser can't decode. Convert it to AAC, MP3 or WAV and import it again.`;
}

/** Image formats browsers cannot decode, with a suggested fix. */
const UNSUPPORTED_IMAGES: Record<string, string> = {
  tif: 'TIFF images',
  tiff: 'TIFF images',
  psd: 'Photoshop files',
  raw: 'camera RAW files',
  cr2: 'camera RAW files',
  cr3: 'camera RAW files',
  nef: 'camera RAW files',
  arw: 'camera RAW files',
  dng: 'camera RAW files',
  svg: 'SVG graphics',
};

export function unsupportedImageMessage(extension: string): string | null {
  const what = UNSUPPORTED_IMAGES[extension.toLowerCase()];
  return what
    ? `${capitalize(what)} can't be used directly. Export it as JPEG or PNG and import that instead.`
    : null;
}

export function unknownFormatMessage(fileName: string): string {
  return `“${fileName}” isn't a video, audio or image format this browser can read.`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
}
